import { FINANCE_CONFIG_WRITE_ROLES, FINANCE_READ_ROLES } from '@/types/shared';
import type { Role } from '@/types/shared';

/**
 * Admissions module write access (register/edit) — students.controller.ts `WRITE_ROLES`. 2026-08:
 * Sales Manager added; they could already create students via lead conversion (`SALES_ROLES` →
 * `POST /leads/{id}/convert`), so both the direct register route and the follow-up edit now match.
 * Soft-delete/restore and branch transfer stay narrower — see the two constants below.
 */
export const ADMISSIONS_WRITE_ROLES: Role[] = ['Admin', 'Admissions Officer', 'Sales Manager'];

/** Soft-delete/restore is Admin-only — students.controller.ts. */
export const ADMISSIONS_DELETE_ROLES: Role[] = ['Admin'];

/** Student branch transfer is Admin-only (§20, DEC-BR17/FR-20) — not even Admissions Officer, who
 * owns student creation/edits otherwise. `POST /students/{studentCode}/transfer-branch`. */
export const STUDENT_BRANCH_TRANSFER_ROLES: Role[] = ['Admin'];

/** Student Affairs module read access — student-affairs.controller.ts `READ_ROLES`. */
export const STUDENT_AFFAIRS_READ_ROLES: Role[] = [
  'Admin',
  'Student Affairs Officer',
  'Admissions Officer',
  'Accounts Officer',
  'Exam Coordinator',
  'Sales Agent',
  'Sales Manager',
];

/**
 * Student Affairs module write access (status/receipt/documents) — student-affairs.controller.ts
 * `WRITE_ROLES`. 2026-08: sales roles gained write access, but NOT on equal terms — a
 * `Sales Manager` writes academy-wide, a `Sales Agent` only for students whose converted lead is
 * assigned to them.
 *
 * Because of that, this constant is the ROUTE-level gate only (who may reach an editable screen at
 * all). It must NOT be used to decide whether a *particular* record is editable: ownership is a
 * per-row fact the client cannot compute, so the server sends it as
 * `StudentAffairsResponse.canEdit` and the detail screen reads that instead. See
 * `StudentAffairsDetailPage.tsx`.
 */
export const STUDENT_AFFAIRS_WRITE_ROLES: Role[] = [
  'Admin',
  'Student Affairs Officer',
  'Sales Manager',
  'Sales Agent',
];

/** Accounts module read access — accounts.controller.ts `READ_ROLES`. */
export const ACCOUNTS_READ_ROLES: Role[] = [
  'Admin',
  'Accounts Officer',
  'Admissions Officer',
  'Student Affairs Officer',
  'Sales Manager',
  'Sales Agent',
];

/** Accounts module write access (fees/installments/payments) — accounts.controller.ts
 * `WRITE_ROLES`. 2026-07: sales roles gained full control of student payments. */
export const ACCOUNTS_WRITE_ROLES: Role[] = [
  'Admin',
  'Accounts Officer',
  'Sales Agent',
  'Sales Manager',
];

/** Exam Bookings module read access — exam-bookings.controller.ts `READ_ROLES`. 2026-07 exam
 * approval workflow: sales roles read exams (to request/track them); accountant retained. */
export const EXAM_BOOKINGS_READ_ROLES: Role[] = [
  'Admin',
  'Exam Coordinator',
  'Admissions Officer',
  'Student Affairs Officer',
  'Accounts Officer',
  'Sales Agent',
  'Sales Manager',
];

/** Exam Bookings module write access (per-section PATCH — book/complete/cancel/metadata) —
 * exam-bookings.controller.ts `WRITE_ROLES`. Sales included, but the server's approval gate stops a
 * sales role reaching 'Booked' until an Admin has approved the request. */
export const EXAM_BOOKINGS_WRITE_ROLES: Role[] = [
  'Admin',
  'Exam Coordinator',
  'Sales Agent',
  'Sales Manager',
];

/** Who can REQUEST an exam (Not Booked → Requested) — exam-bookings.controller.ts `REQUEST_ROLES`. */
export const EXAM_REQUEST_ROLES: Role[] = ['Admin', 'Exam Coordinator', 'Sales Agent', 'Sales Manager'];

/** Who can APPROVE/REJECT an exam request — exam-bookings.controller.ts `APPROVE_ROLES` (Admin only). */
export const EXAM_APPROVE_ROLES: Role[] = ['Admin'];

/** Sales Follow-up (leads) module read/write access — sales.controller.ts `STAFF_ROLES`. Own-scope
 * (a Sales Agent only ever sees/edits their own assigned leads) is enforced server-side; the client
 * never filters by assignee for this role, it just renders whatever the API returns. */
export const SALES_ROLES: Role[] = ['Admin', 'Sales Manager', 'Sales Agent', 'Accounts Officer'];

/** Sales Follow-up manage access (delete, assign/reassign, reopening a Lost lead) —
 * sales.controller.ts `MANAGE_ROLES`. */
export const SALES_MANAGE_ROLES: Role[] = ['Admin', 'Sales Manager'];

/** Lead restore is Admin-only — sales.controller.ts `restore()`. */
export const SALES_RESTORE_ROLES: Role[] = ['Admin'];

/** Sales reporting endpoints — sales-reports.controller.ts `REPORT_ROLES`. 2026-07: Sales Manager
 * removed (sales roles restricted to students/leads/student-affairs/payments) — Admin-only now. */
export const SALES_REPORT_ROLES: Role[] = ['Admin'];

/** Financial P&L report page (`/reports/finance`, 2026-07) — Admin only. Reuses `GET /finance/revenue`
 * (which is dual-role), but the report screen itself is gated to Admin per the requirement. */
export const FINANCE_REPORT_ROLES: Role[] = ['Admin'];

/** Settings → Lookups management screen (`/settings/lookups`). The `GET /lookups/*` endpoints
 * themselves have no role restriction (any authenticated role reads them for form dropdowns), but
 * every write (`POST`/`PATCH`/`DELETE`) is `@Roles('Admin')` — see grades/schools/
 * educational-administrations controllers. The *management* screen is gated Admin-only rather than
 * read-only-for-everyone-else, since it has no purpose for non-Admin roles. */
export const SETTINGS_LOOKUPS_ROLES: Role[] = ['Admin'];

/** Settings → Users management screen (`/settings/users`, T70/T71). Every `/users/*` endpoint is
 * `@Roles('Admin')` (list/create/read/update/delete/restore) except `PATCH /users/{id}/password`,
 * which is authorized in the service itself (Admin, or the caller changing their own password) —
 * see users.controller.ts. The screen itself has no purpose for non-Admin roles, so it's gated
 * Admin-only rather than read-only-for-everyone-else, mirroring `SETTINGS_LOOKUPS_ROLES`. */
export const SETTINGS_USERS_ROLES: Role[] = ['Admin'];

/** Settings → Finance management screen (`/settings/finance`, T125 — exam-pricing + per-school-cost
 * config). Reuses `FINANCE_CONFIG_WRITE_ROLES` (Admin only, `finance.enum.ts`, DEC-FIN1) rather than
 * hardcoding the array a second time — that constant is also the backend's single source of truth
 * for the `PUT /finance/exam-pricing*`/`PUT /finance/school-costs*` write RBAC. `GET /finance/*` is
 * dual-role (Admin + Accounts Officer), but this screen exists only to edit config, so — mirroring
 * the existing `SETTINGS_USERS_ROLES`/`SETTINGS_LOOKUPS_ROLES` precedent — it's gated Admin-only
 * rather than read-only-for-Accounts-Officer (their inline reference access is served on the
 * separate `/finance` overview screen, not here — 17-finance-ux.md §10.1). */
export const SETTINGS_FINANCE_ROLES: Role[] = FINANCE_CONFIG_WRITE_ROLES;

/**
 * Who may OPEN `/settings/finance` (route + nav visibility). 2026-07: Accounts Officer (the
 * "accountant") may now VIEW finance settings but not edit — so this is the read tier (Admin +
 * Accounts Officer), while the managers keep gating every edit control behind
 * `SETTINGS_FINANCE_ROLES` (Admin only). The `GET /finance/*` endpoints already allow this tier,
 * so no server change is needed — a non-Admin simply sees the config read-only. */
export const SETTINGS_FINANCE_VIEW_ROLES: Role[] = FINANCE_READ_ROLES;

/** Settings → Content management screen (`/settings/content`, T140 — Parents' Opinions + FAQ).
 * Every `/content/parent-opinions/*` and `/content/faq/*` endpoint is `@Roles('Admin')` (list
 * included, D-3 — tighter than the lookups list, which is any-authenticated), while the public
 * `/public/content/*` reads are unauthenticated (DEC-WC7). The screen itself has no purpose for
 * non-Admin roles, so it's gated Admin-only, mirroring `SETTINGS_LOOKUPS_ROLES`/
 * `SETTINGS_USERS_ROLES`. */
export const SETTINGS_CONTENT_ROLES: Role[] = ['Admin'];

export function hasRole(role: Role | undefined, allowed: Role[]): boolean {
  return Boolean(role && allowed.includes(role));
}
